import { type OptionProps } from '@amelio/ui-library';

import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { CycleEvaluationRow } from '@/components/CycleEvaluationRow/CycleEvaluationRow';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import {
    getGroupEvaluationTypes,
    getTypeBadgeLabelKey,
    selectGroupEvaluationTemplates,
} from '@/pages/CycleEvaluation/Dashboard/cycleListRows';
import {
    getGroupStatusLabelKey,
    getGroupStatusTone,
} from '@/pages/CycleEvaluation/Dashboard/cycleStatus';
import { useEvaluationPeriodLabel } from '@/pages/CycleEvaluation/Dashboard/useEvaluationPeriodLabel';
import { getLocalizedText } from '@/utils/localization';

interface CycleEvaluationGroupRowProps {
    group: CycleEvaluationGroup;
    actionMenuItems: (group: CycleEvaluationGroup) => OptionProps[];
    /** Unfiltered evaluation list, used to resolve the template names of the cycle's children. */
    allCycleEvaluations?: CycleEvaluation[];
    onRowClick?: () => void;
}

export const CycleEvaluationGroupRow = ({
    group,
    actionMenuItems,
    allCycleEvaluations,
    onRowClick,
}: CycleEvaluationGroupRowProps) => {
    const { t } = useAmelioTranslations();
    const getEvaluationPeriodLabel = useEvaluationPeriodLabel();
    const replaceDynamicTags = useReplaceDynamicTags();

    // The cycle name titles the row; the template names qualify it on a labelled secondary line so
    // an administrator can tell two cycles built on the same template apart.
    const templateNames = Array.from(
        new Set(
            selectGroupEvaluationTemplates({
                group,
                cycleEvaluations: allCycleEvaluations ?? [],
            })
                .map(({ title }) => replaceDynamicTags({ text: getLocalizedText(title) }))
                .filter((name) => name.length > 0),
        ),
    );

    return (
        <CycleEvaluationRow
            dataTestId={'cycle-evaluation-group-row'}
            actionMenuItems={actionMenuItems(group)}
            title={group.name}
            period={getEvaluationPeriodLabel({
                start: group.evaluationPeriodStart,
                end: group.evaluationPeriodEnd,
            })}
            detail={
                templateNames.length > 0
                    ? {
                          label: t('cycleEvaluationDashboard.evaluationTemplateLabel'),
                          value: templateNames.join(' · '),
                      }
                    : undefined
            }
            statusTone={getGroupStatusTone(group.status)}
            statusLabel={t(getGroupStatusLabelKey(group.status))}
            typeLabels={getGroupEvaluationTypes(group).map((type) => t(getTypeBadgeLabelKey(type)))}
            onRowClick={onRowClick}
        />
    );
};
